// frontend/src/components/formularios/campos/SeleccionObjetivoPadre.jsx
import React, { useState, useEffect } from 'react';
import {
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Box,
  Typography,
  Chip,
  CircularProgress,
  Alert,
  Paper,
  ListItemText,
  ListItemIcon,
  Divider,
  FormControlLabel,
  Checkbox,
  Button,
  Stack,
  Badge,
  Tooltip
} from '@mui/material';
import {
  Assignment as AssignmentIcon,
  Check as CheckIcon,
  Block as BlockIcon,
  Warning as WarningIcon,
  CheckCircle as CheckCircleIcon,
  RadioButtonUnchecked as RadioButtonUncheckedIcon
} from '@mui/icons-material';
import formularioService from '../../../services/formularioService';

const SeleccionObjetivoPadre = ({ 
  campo, 
  value, 
  onChange, 
  parentRespuestaId,
  disabled = false,
  respuestasAnteriores = [],
  multiselect = false,
  maxSelecciones = 15,
  mostrarEstado = false,
  modoVisualizacion = 'lista' // 'lista' o 'chips'
}) => {
  const [loading, setLoading] = useState(true);
  const [objetivos, setObjetivos] = useState([]);
  const [objetivosUsados, setObjetivosUsados] = useState([]);
  const [historialObjetivos, setHistorialObjetivos] = useState({});
  const [error, setError] = useState(null);

  // Para multiselección
   const valoresSeleccionados = multiselect ? 
    (Array.isArray(value) ? value : []) : 
    (value ? [value] : []);

  useEffect(() => {
  if (parentRespuestaId) {
    cargarObjetivos(parentRespuestaId);
  } else {
    // Intentar buscar el formulario padre automáticamente
    buscarFormularioPadre();
  }
}, [parentRespuestaId]);

const buscarFormularioPadre = async () => {
  try {
    setLoading(true);
    setError(null);
    
    const historiaEspecialidadId = campo.historia_especialidad_id || null;
    
    if (!historiaEspecialidadId) {
      setError('No se puede buscar el plan general sin el ID de historia');
      return;
    }
    
    const response = await formularioService.buscarPlanGeneralReciente(historiaEspecialidadId);
    
    if (response.success && response.data?.respuesta_id) {
      const padreId = response.data.respuesta_id;
      cargarObjetivos(padreId);
    } else {
      setError('No se encontró un plan general cerrado. Por favor, complete primero el plan de intervención.');
    }
  } catch (err) {
    console.error('Error al buscar formulario padre:', err);
    setError('Error al buscar el plan general');
  } finally {
    setLoading(false);
  }
};
  
  const cargarObjetivos = async (padreId) => {
  try {
    setLoading(true);
    setError(null);
    
    const idToUse = padreId || parentRespuestaId;
     
    if (!idToUse) {
      console.error('No hay ID de padre para cargar objetivos');
      setError('No se especificó un formulario padre');
      return;
    }
    
    const response = await formularioService.getObjetivosDisponibles(idToUse);
     
    if (response.success && response.data) {
      setObjetivos(response.data.objetivos_disponibles || []);
      setObjetivosUsados(response.data.objetivos_usados || []);
    } else {
      throw new Error('No se pudieron cargar los objetivos');
    }
  } catch (err) {
    console.error('Error al cargar objetivos:', err);
    setError('Error al cargar los objetivos disponibles');
  } finally {
    setLoading(false);
  }
};
  
  const esObjetivoDisponible = (objetivoId) => {
    // Verificar si ya fue usado en respuestas anteriores
    if (objetivosUsados.includes(objetivoId)) {
      const historial = historialObjetivos[objetivoId];
      // Si tiene historial y el último estado es "logrado", no está disponible
      if (historial && historial.ultimo_estado === 'logrado') {
        return false;
      }
    }
    
    return true;
  };
  
  const obtenerEstadoObjetivo = (objetivoId) => {
    const historial = historialObjetivos[objetivoId];
    if (!historial) return null;
    
    return {
      estado: historial.ultimo_estado,
      consecutivosEnProceso: historial.consecutivos_en_proceso || 0,
      vecesLogrado: historial.veces_logrado || 0,
      ultimaFecha: historial.ultima_fecha
    };
  };
  
  const handleChange = (event) => {
    if (multiselect) {
      // Manejo para multiselección con chips
      const nuevosValores = event.target.value;
      if (nuevosValores.length <= maxSelecciones) {
        onChange(nuevosValores);
      }
    } else {
      // Manejo simple (original)
      const objetivoId = event.target.value;
      onChange(objetivoId || '');
    }
  };
  
  const handleCheckboxChange = (objetivoId, checked) => {
    if (checked) {
      if (valoresSeleccionados.length < maxSelecciones) {
        const nuevosValores = [...valoresSeleccionados, objetivoId];
        onChange(nuevosValores);
      }
    } else {
      const nuevosValores = valoresSeleccionados.filter(id => id !== objetivoId);
      onChange(nuevosValores);
    }
  };
  
  if (loading) {
    return (
      <Box display="flex" alignItems="center" justifyContent="center" p={2}>
        <CircularProgress size={24} />
        <Typography variant="body2" sx={{ ml: 1 }}>
          Cargando objetivos...
        </Typography>
      </Box>
    );
  }
  
  if (error) {
    return (
      <Alert severity="error" sx={{ mb: 2 }}>
        {error}
      </Alert>
    );
  }
  
  if (objetivos.length === 0) {
    return (
      <Alert severity="info" sx={{ mb: 2 }}>
        No hay objetivos disponibles. Verifique que el plan terapéutico tenga objetivos definidos.
      </Alert>
    );
  }
  
  // Agrupar objetivos por área
  const objetivosPorArea = objetivos.reduce((acc, obj) => {
    if (!acc[obj.area_nombre]) {
      acc[obj.area_nombre] = [];
    }
    acc[obj.area_nombre].push(obj);
    return acc;
  }, {});
  
  const objetivosDisponibles = objetivos.filter(obj => esObjetivoDisponible(obj.id));
  
  // Renderizado para multiselección con checkboxes
  if (true) {
    
    return (
      <Box>
        <Box mb={2} display="flex" justifyContent="space-between" alignItems="center">
          <Typography variant="subtitle1" fontWeight="bold">
            {campo.etiqueta || 'Seleccionar objetivos'}
            {campo.requerido && ' *'}
          </Typography>
          <Stack direction="row" spacing={1}>
            <Chip 
              label={`${valoresSeleccionados.length}/${maxSelecciones} seleccionados`}
              color={valoresSeleccionados.length > 0 ? "primary" : "default"}
              size="small"
            />
            {valoresSeleccionados.length > 0 && (
              <Button 
                size="small" 
                onClick={() => onChange([])}
                color="secondary"
              >
                Limpiar
              </Button>
            )}
          </Stack>
        </Box>
        
        <Paper variant="outlined" sx={{ maxHeight: 400, overflow: 'auto' }}>
          {Object.entries(objetivosPorArea).map(([area, objetivosArea], indexArea) => (
            <Box key={area}>
              {indexArea > 0 && <Divider />}
              
              <Box sx={{ p: 2, bgcolor: 'primary.50' }}>
                <Typography variant="subtitle2" color="primary" fontWeight="bold">
                  {area}
                </Typography>
              </Box>
              
              <Box sx={{ p: 1 }}>
                {objetivosArea.map((objetivo) => {
                  const disponible = esObjetivoDisponible(objetivo.id);
                  const seleccionado = valoresSeleccionados.includes(objetivo.id);
                  const estadoInfo = mostrarEstado ? obtenerEstadoObjetivo(objetivo.id) : null;
                  
                  return (
                    <Box 
                      key={objetivo.id}
                      sx={{ 
                        borderRadius: 1,
                        '&:hover': { bgcolor: 'action.hover' },
                        opacity: disponible ? 1 : 0.6
                      }}
                    >
                      <FormControlLabel
                        control={
                          <Checkbox
                            checked={seleccionado}
                            onChange={(e) => handleCheckboxChange(objetivo.id, e.target.checked)}
                            disabled={!disponible || disabled || 
                                    (!seleccionado && valoresSeleccionados.length >= maxSelecciones)}
                            color="primary"
                          />
                        }
                        label={
                          <Box sx={{ py: 1 }}>
                            <Box display="flex" alignItems="center" gap={1}>
                              <Typography variant="body2">
                                <strong>Objetivo {objetivo.objetivo_numero}:</strong> {objetivo.objetivo_texto}
                              </Typography>
                              
                              {estadoInfo && (
                                <Stack direction="row" spacing={0.5}>
                                  {estadoInfo.estado === 'logrado' && (
                                    <Chip 
                                      icon={<CheckCircleIcon />}
                                      label="Logrado"
                                      size="small"
                                      color="success"
                                    />
                                  )}
                                  
                                  {estadoInfo.consecutivosEnProceso >= 2 && (
                                    <Tooltip title="Requiere análisis funcional">
                                      <Chip 
                                        icon={<WarningIcon />}
                                        label={`${estadoInfo.consecutivosEnProceso} en proceso`}
                                        size="small"
                                        color="warning"
                                      />
                                    </Tooltip>
                                  )}
                                </Stack>
                              )}
                            </Box>
                            
                            {!disponible && (
                              <Typography variant="caption" color="error">
                                Ya completado o bloqueado
                              </Typography>
                            )}
                          </Box>
                        }
                        sx={{ width: '100%', m: 0, px: 1 }}
                      />
                    </Box>
                  );
                })}
              </Box>
            </Box>
          ))}
        </Paper>
        
        {/* Resumen de selección */}
        {valoresSeleccionados.length > 0 && (
          <Box mt={2}>
            <Typography variant="caption" color="textSecondary" gutterBottom>
              Objetivos seleccionados para trabajar:
            </Typography>
            <Stack direction="row" spacing={0.5} flexWrap="wrap" sx={{ mt: 1 }}>
              {valoresSeleccionados.map(id => {
                const obj = objetivos.find(o => o.id === id);
                return obj ? (
                  <Chip
                    key={id}
                    label={`${obj.area_nombre} - Obj. ${obj.objetivo_numero}`}
                    size="small"
                    onDelete={() => handleCheckboxChange(id, false)}
                    color="primary"
                    variant="outlined"
                  />
                ) : null;
              })}
            </Stack>
          </Box>
        )}
      </Box>
    );
  }
  
  
  // Renderizado original para selección simple o multiselección con Select
  return (
    <Box>
      <FormControl fullWidth variant="outlined" disabled={disabled}>
        <InputLabel id={`${campo.id}-label`}>
          {campo.etiqueta}
          {campo.requerido && ' *'}
        </InputLabel>
        <Select
          labelId={`${campo.id}-label`}
          id={campo.id}
          value={multiselect ? valoresSeleccionados : (value || '')}
          onChange={handleChange}
          label={campo.etiqueta + (campo.requerido ? ' *' : '')}
          multiple={multiselect}
          sx={{ minHeight: 56 }}
          renderValue={multiselect ? (selected) => (
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
              {selected.map((value) => {
                const obj = objetivos.find(o => o.id === value);
                return obj ? (
                  <Chip key={value} label={`${obj.area_nombre} - Obj. ${obj.objetivo_numero}`} size="small" />
                ) : null;
              })}
            </Box>
          ) : undefined}
        >
          <MenuItem value="">
            <em>Seleccione {multiselect ? 'objetivos' : 'un objetivo'}</em>
          </MenuItem>
          
          {Object.entries(objetivosPorArea).map(([area, objetivosArea]) => [
            <MenuItem key={`area-${area}`} disabled sx={{ fontWeight: 'bold', backgroundColor: '#f5f5f5' }}>
              <Typography variant="subtitle2" color="primary">
                {area}
              </Typography>
            </MenuItem>,
            ...objetivosArea.map((objetivo) => {
              const disponible = esObjetivoDisponible(objetivo.id);
              
              return (
                <MenuItem 
                  key={objetivo.id} 
                  value={objetivo.id}
                  disabled={!disponible}
                  sx={{ pl: 4 }}
                >
                  <ListItemIcon sx={{ minWidth: 36 }}>
                    {disponible ? (
                      valoresSeleccionados.includes(objetivo.id) ? (
                        <CheckIcon color="success" fontSize="small" />
                      ) : (
                        <AssignmentIcon fontSize="small" />
                      )
                    ) : (
                      <BlockIcon color="error" fontSize="small" />
                    )}
                  </ListItemIcon>
                  <ListItemText
                    primary={
                      <Box>
                        <Typography variant="body2" component="span">
                          Objetivo {objetivo.objetivo_numero}: 
                        </Typography>
                        <Typography variant="body2" component="span" sx={{ ml: 1 }}>
                          {objetivo.objetivo_texto}
                        </Typography>
                      </Box>
                    }
                    secondary={
                      !disponible && (
                        <Typography variant="caption" color="error">
                          Ya trabajado o completado
                        </Typography>
                      )
                    }
                  />
                </MenuItem>
              );
            }),
            <Divider key={`divider-${area}`} />
          ])}
        </Select>
      </FormControl>
      
      {/* Mostrar resumen de objetivos */}
      <Box sx={{ mt: 2 }}>
        <Box display="flex" gap={1} flexWrap="wrap">
          <Chip 
            label={`${objetivosDisponibles.length} disponible${objetivosDisponibles.length !== 1 ? 's' : ''}`}
            size="small" 
            color="success" 
            variant="outlined" 
          />
          <Chip 
            label={`${objetivosUsados.length} trabajado${objetivosUsados.length !== 1 ? 's' : ''}`}
            size="small" 
            color="default" 
            variant="outlined" 
          />
          <Chip 
            label={`${objetivos.length} total${objetivos.length !== 1 ? 'es' : ''}`}
            size="small" 
            color="primary" 
            variant="outlined" 
          />
        </Box>
      </Box>
    </Box>
  );
};

export default SeleccionObjetivoPadre;